import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/#services" },
  { label: "Solutions", to: "/#solutions" },
  { label: "Our Work", to: "/work" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  const go = (e, to) => {
    e.preventDefault();
    setOpen(false);
    if (to.includes("#")) {
      const [path, id] = to.split("#");
      if (location.pathname === (path || "/")) {
        const el = document.getElementById(id);
        if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    navigate(to);
  };

  const isActive = (to) => !to.includes("#") && location.pathname === to;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-panel shadow-2xl shadow-blue-950/30" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 flex items-center justify-between h-[72px]">
        <Link to="/" aria-label="Trendsight Media home" data-testid="nav-logo-link">
          <Logo compact={scrolled} />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-menu">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.to}
              onClick={(e) => go(e, l.to)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[13px] font-medium tracking-wide transition-colors duration-300 hover:text-blue-400 ${
                isActive(l.to) ? "text-blue-400" : "text-slate-300"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/start"
            data-testid="nav-start-project-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-[13px] font-semibold px-5 py-2.5 transition-all duration-300 active:scale-95 shadow-lg shadow-blue-600/25"
          >
            Start a Project
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          className="lg:hidden text-slate-200 p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-hamburger-btn"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden glass-panel border-t border-slate-800/60"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.label}
                  href={l.to}
                  onClick={(e) => go(e, l.to)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display text-2xl font-bold text-slate-100 py-2.5 hover:text-blue-400 transition-colors"
                >
                  {l.label}
                </motion.a>
              ))}
              <Link
                to="/start"
                data-testid="mobile-start-project-btn"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 text-white font-semibold px-6 py-3.5"
              >
                Start a Project <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
